import React, { useEffect, useState, useRef } from 'react';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { CheckCircleIcon, SendIcon, LockIcon, UsersIcon, FileTextIcon, ZapIcon, MessageCircleIcon, ClockIcon } from 'lucide-react';
import { track, trackCustom, trackFormView } from '../tracking/metaPixel';
import { useLanguage } from '../../context/LanguageContext';
import { sendWhatsAppNotification } from '../../utils/whatsappNotification';
import { sendToGoogleSheets } from '../../utils/googleSheets';
import { getWhatsAppLink } from '../../utils/whatsappLink';
const LeadForm = () => {
  const {
    t,
    language
  } = useLanguage();
  const [formState, setFormState] = useState({
    name: '',
    phone: '',
    email: '',
    vehicle: '',
    creditScore: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [countdown, setCountdown] = useState(7);
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [formStarted, setFormStarted] = useState(false);
  const [formViewed, setFormViewed] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [redirectAttempted, setRedirectAttempted] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  // Check if user already submitted (on mount)
  useEffect(() => {
    const submitted = localStorage.getItem('formSubmitted');
    const submittedTime = localStorage.getItem('formSubmittedTime');
    if (submitted === 'true' && submittedTime) {
      const hoursSince = (Date.now() - parseInt(submittedTime)) / (1000 * 60 * 60);
      if (hoursSince < 24) {
        setAlreadySubmitted(true);
        setSubmitStatus('success');
        setCountdown(0);
        setRedirectAttempted(true);
        const savedName = localStorage.getItem('formSubmittedName') || '';
        const savedVehicle = localStorage.getItem('formSubmittedVehicle') || '';
        const firstName = savedName.split(' ')[0] || 'cliente';
        const vehicleText = savedVehicle || (language === 'es' ? 'un vehículo' : 'a vehicle');
        const whatsappMessage = language === 'es' ? `Hola Pedro, soy ${firstName}. Ya envié mi solicitud. Me interesa el ${vehicleText}. ¿Cuándo podemos hablar?` : `Hi Pedro, I'm ${firstName}. I already submitted my request. I'm interested in the ${vehicleText}. When can we talk?`;
        setWhatsappUrl(getWhatsAppLink(whatsappMessage));
        setFormState(prev => ({
          ...prev,
          name: savedName,
          vehicle: savedVehicle
        }));
      } else {
        localStorage.removeItem('formSubmitted');
        localStorage.removeItem('formSubmittedTime');
        localStorage.removeItem('formSubmittedName');
        localStorage.removeItem('formSubmittedVehicle');
        localStorage.removeItem('formSubmittedPhone');
      }
    }
  }, [language]);
  // Trust badges content
  const trustBadges = {
    es: [{
      icon: <UsersIcon className="w-5 h-5" data-id="element-178" />,
      text: '500+ familias'
    }, {
      icon: <FileTextIcon className="w-5 h-5" data-id="element-179" />,
      text: 'Programas ITIN'
    }, {
      icon: <ZapIcon className="w-5 h-5" data-id="element-180" />,
      text: 'Aprobación mismo día'
    }, {
      icon: <MessageCircleIcon className="w-5 h-5" data-id="element-181" />,
      text: 'En español'
    }],
    en: [{
      icon: <UsersIcon className="w-5 h-5" data-id="element-182" />,
      text: '500+ families'
    }, {
      icon: <FileTextIcon className="w-5 h-5" data-id="element-183" />,
      text: 'ITIN programs'
    }, {
      icon: <ZapIcon className="w-5 h-5" data-id="element-184" />,
      text: 'Same-day approval'
    }, {
      icon: <MessageCircleIcon className="w-5 h-5" data-id="element-185" />,
      text: 'In Spanish'
    }]
  };
  const currentBadges = trustBadges[language];
  // Track when form section becomes visible
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !formViewed) {
          setFormViewed(true);
          trackFormView({
            language
          });
        }
      });
    }, {
      threshold: 0.3
    });
    if (formRef.current) {
      observer.observe(formRef.current);
    }
    return () => observer.disconnect();
  }, [formViewed, language]);
  // Track when user starts filling the form (first interaction)
  const handleFormInteraction = () => {
    if (!formStarted) {
      setFormStarted(true);
      trackCustom('FormStart', {
        content_name: 'Lead Form',
        language
      });
    }
    // Progressive disclosure: show all fields once user interacts
    if (!showAllFields) {
      setShowAllFields(true);
    }
  };
  const handleFormSubmitSuccess = () => {
    try {
      // Check for duplicate by phone number
      const previousPhone = localStorage.getItem('formSubmittedPhone');
      const cleanPhone = formState.phone.replace(/\D/g, '');
      const isDuplicate = previousPhone === cleanPhone;
      // Only fire Lead event if NOT a duplicate
      if (!isDuplicate) {
        track('Lead', {
          content_name: 'Website Lead Form',
          submit: 'success',
          language,
          credit_score: formState.creditScore,
          vehicle: formState.vehicle
        });
      }
      const leadData = {
        name: formState.name,
        phone: formState.phone,
        email: formState.email,
        vehicle: formState.vehicle,
        creditScore: formState.creditScore,
        language,
        source: isDuplicate ? 'Landing Page - DUPLICATE' : 'Landing Page - Lead Form',
        timestamp: new Date().toLocaleString('es-US', {
          timeZone: 'America/New_York',
          dateStyle: 'medium',
          timeStyle: 'short'
        })
      };
      // Send to Google Sheets (non-blocking) — marks duplicates in source field
      sendToGoogleSheets(leadData);
      // Only send WhatsApp notification for NEW leads (not duplicates)
      if (!isDuplicate) {
        sendWhatsAppNotification(leadData);
      }
      localStorage.setItem('formSubmitted', 'true');
      localStorage.setItem('formSubmittedTime', Date.now().toString());
      localStorage.setItem('formSubmittedName', formState.name);
      localStorage.setItem('formSubmittedVehicle', formState.vehicle);
      localStorage.setItem('formSubmittedPhone', cleanPhone);
      // Build personalized WhatsApp message with lead info
      const firstName = formState.name.split(' ')[0];
      const vehicleText = formState.vehicle || (language === 'es' ? 'un vehículo' : 'a vehicle');
      const whatsappMessage = language === 'es' ? `Hola Pedro, soy ${firstName}. Acabo de enviar mi solicitud desde la web. Me interesa el ${vehicleText}. ¿Cuándo podemos hablar?` : `Hi Pedro, I'm ${firstName}. I just submitted my request on the website. I'm interested in the ${vehicleText}. When can we talk?`;
      const waUrl = getWhatsAppLink(whatsappMessage);
      setWhatsappUrl(waUrl);
      // Countdown timer
      let count = 7;
      const timer = setInterval(() => {
        count--;
        setCountdown(count);
        if (count <= 0) {
          clearInterval(timer);
          setRedirectAttempted(true);
          // Track Contact event when auto-redirect to WhatsApp happens
          track('Contact', {
            content_name: 'WhatsApp Auto-Redirect',
            method: 'whatsapp',
            language
          });
          // Open WhatsApp in new tab instead of replacing current page
          try {
            const newWindow = window.open(waUrl, '_blank');
            // If popup was blocked, user still has the button
            if (!newWindow || newWindow.closed) {
              console.log('Popup blocked — user can click the WhatsApp button manually');
            }
          } catch (err) {
            console.log('Redirect failed — user can click the WhatsApp button manually');
          }
        }
      }, 1000);
    } catch (error) {
      console.error('Error al redireccionar:', error);
    }
  };
  const vehicleOptions = [{
    value: 'Subaru Impreza',
    label: 'Subaru Impreza'
  }, {
    value: 'Subaru Crosstrek',
    label: 'Subaru Crosstrek'
  }, {
    value: 'Subaru Outback',
    label: 'Subaru Outback'
  }, {
    value: 'GMC Acadia',
    label: 'GMC Acadia'
  }, {
    value: 'GMC Yukon',
    label: 'GMC Yukon'
  }, {
    value: 'GMC Sierra',
    label: 'GMC Sierra'
  }, {
    value: 'Buick Enclave',
    label: 'Buick Enclave'
  }, {
    value: 'Hummer EV',
    label: 'Hummer EV'
  }, {
    value: 'Hyundai Elantra',
    label: 'Hyundai Elantra'
  }, {
    value: 'Hyundai Tucson',
    label: 'Hyundai Tucson'
  }, {
    value: 'Hyundai Santa Fe',
    label: 'Hyundai Santa Fe'
  }, {
    value: 'Otro',
    label: language === 'es' ? 'Otro / No estoy seguro' : 'Other / Not sure'
  }];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    handleFormInteraction();
    const {
      name,
      value
    } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formState.name) newErrors.name = t.leadForm.fields.name.error;
    if (!formState.phone) newErrors.phone = t.leadForm.fields.phone.error;else if (formState.phone.replace(/\D/g, '').length < 10) newErrors.phone = t.leadForm.fields.phone.error;
    if (!formState.email) newErrors.email = t.leadForm.fields.email.error;
    if (!formState.vehicle) newErrors.vehicle = t.leadForm.fields.vehicle.error;
    if (!formState.creditScore) newErrors.creditScore = t.leadForm.fields.creditScore.error;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  return <section id="lead-form" className="py-16 px-4 bg-white" ref={formRef} data-id="element-186">
      <div className="container mx-auto" data-id="element-187">
        <div className="max-w-xl mx-auto" data-id="element-188">
          {/* Trust Authority Block - NEW */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-id="element-189">
            {currentBadges.map((badge, idx) => <div key={idx} className="bg-gray-50 border border-gray-100 rounded-lg p-3 flex flex-col items-center text-center justify-center shadow-sm" data-id="element-190">
                <div className="text-primary mb-1.5" data-id="element-191">{badge.icon}</div>
                <span className="text-xs font-semibold text-gray-700 leading-tight" data-id="element-192">
                  {badge.text}
                </span>
              </div>)}
          </div>

          <div className="bg-white rounded-2xl shadow-custom border border-sand-200 p-6 md:p-10" data-id="element-193">
            <div className="text-center mb-8" data-id="element-194">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-primary mb-2" data-id="element-195">
                {t.leadForm.headline}
              </h2>
              <p className="text-gray-600" data-id="element-196">{t.leadForm.subheadline}</p>

              {/* Qualification Micro-copy - NEW */}
              <p className="text-xs text-gray-400 italic mt-2" data-id="element-197">
                {language === 'es' ? 'Para compradores serios listos para explorar opciones de financiamiento' : 'For serious buyers ready to explore financing options'}
              </p>
            </div>

            {submitStatus === 'success' ? <div className="text-center py-6" data-id="element-198">
                {/* Success Icon with pulse animation */}
                <div className="relative mx-auto mb-5 w-20 h-20" data-id="element-199">
                  <div className="absolute inset-0 bg-green-400/20 rounded-full animate-ping" data-id="element-200" />
                  <div className="relative bg-green-500 text-white w-20 h-20 rounded-full flex items-center justify-center" data-id="element-201">
                    <CheckCircleIcon className="w-10 h-10" data-id="element-202" />
                  </div>
                </div>

                {/* Main headline */}
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-203">
                  {language === 'es' ? '🎉 ¡Excelente, ' : '🎉 Excellent, '}
                  {formState.name.split(' ')[0]}!
                </h3>

                {/* Urgency subheadline */}
                <p className="text-lg font-semibold text-green-600 mb-1" data-id="element-204">
                  {language === 'es' ? '✅ Tu solicitud fue recibida' : '✅ Your request was received'}
                </p>

                {/* Next step copy */}
                <p className="text-gray-600 mb-6 max-w-sm mx-auto" data-id="element-205">
                  {language === 'es' ? 'Para darte una respuesta más rápida, conéctate con Pedro directamente por WhatsApp 👇' : 'For a faster response, connect with Pedro directly on WhatsApp 👇'}
                </p>

                {/* WhatsApp CTA Button */}
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => {
              track('Contact', {
                content_name: 'WhatsApp Button Click - Success Screen',
                method: 'whatsapp',
                language
              });
            }} className="inline-flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#1fb855] text-white font-bold text-lg px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 w-full max-w-sm mx-auto" data-id="element-206">
                  <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" data-id="element-207">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-208" />
                  </svg>
                  {language === 'es' ? '💬 Chatear con Pedro ahora' : '💬 Chat with Pedro now'}
                </a>

                {/* Countdown redirect */}
                {!redirectAttempted ? <div className="flex items-center justify-center gap-2 mt-5 text-sm text-gray-400" data-id="element-209">
                    <ClockIcon className="w-4 h-4 animate-spin" style={{
                animationDuration: '3s'
              }} data-id="element-210" />
                    <span data-id="element-211">
                      {language === 'es' ? `Redirigiendo a WhatsApp en ${countdown}s...` : `Redirecting to WhatsApp in ${countdown}s...`}
                    </span>
                  </div> : <div className="mt-5 text-sm text-gray-500 bg-gray-50 rounded-lg p-3" data-id="element-212">
                    <p className="font-medium mb-1" data-id="element-213">
                      {language === 'es' ? '¿No se abrió WhatsApp?' : "WhatsApp didn't open?"}
                    </p>
                    <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-[#25D366] font-bold underline" onClick={() => {
                track('Contact', {
                  content_name: 'WhatsApp Fallback Link',
                  method: 'whatsapp',
                  language
                });
              }} data-id="element-214">
                      {language === 'es' ? '👉 Haz clic aquí para abrir WhatsApp' : '👉 Click here to open WhatsApp'}
                    </a>
                  </div>}

                {/* Trust reinforcement */}
                <p className="text-xs text-gray-400 mt-4" data-id="element-215">
                  {language === 'es' ? '🔒 Pedro responde en menos de 5 minutos durante horario laboral' : '🔒 Pedro responds within 5 minutes during business hours'}
                </p>
              </div> : <form onSubmit={e => {
            e.preventDefault();
            if (!validateForm()) {
              return;
            }
            setIsSubmitting(true);
            setSubmitStatus('success');
            handleFormSubmitSuccess();
          }} className="space-y-5" data-id="element-216">
                {/* Name - Always visible */}
                <Input label={t.leadForm.fields.name.label} type="text" id="name" name="name" placeholder={t.leadForm.fields.name.placeholder} value={formState.name} onChange={handleChange} required error={errors.name} data-id="element-217" />

                {/* Phone - Always visible */}
                <Input label={t.leadForm.fields.phone.label} type="tel" id="phone" name="phone" placeholder={t.leadForm.fields.phone.placeholder} value={formState.phone} onChange={handleChange} required error={errors.phone} data-id="element-218" />

                {/* Progressive Disclosure Fields */}
                <div className={`space-y-5 transition-all duration-500 ease-in-out overflow-hidden ${showAllFields ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-219">
                  {/* Credit Score */}
                  <Select label={t.leadForm.fields.creditScore.label} id="creditScore" name="creditScore" options={t.leadForm.fields.creditScore.options} value={formState.creditScore} onChange={handleChange} placeholder={t.leadForm.fields.creditScore.placeholder} required={showAllFields} // Only required if shown
              error={errors.creditScore} data-id="element-220" />

                  {/* Vehicle */}
                  <Select label={t.leadForm.fields.vehicle.label} id="vehicle" name="vehicle" options={vehicleOptions} value={formState.vehicle} onChange={handleChange} placeholder={t.leadForm.fields.vehicle.placeholder} required={showAllFields} // Only required if shown
              error={errors.vehicle} data-id="element-221" />

                  {/* Email */}
                  <Input label={t.leadForm.fields.email.label} type="email" id="email" name="email" placeholder={t.leadForm.fields.email.placeholder} value={formState.email} onChange={handleChange} required={showAllFields} // Only required if shown
              error={errors.email} data-id="element-222" />
                </div>

                <input type="hidden" name="language" value={language} data-id="element-223" />

                <Button type="submit" variant="primary" size="lg" className="w-full bg-primary hover:bg-secondary mt-2" loading={isSubmitting} data-id="element-224">
                  <SendIcon className="w-4 h-4 mr-2" data-id="element-225" /> {t.leadForm.cta}
                </Button>

                <div className="flex items-center justify-center gap-2 mt-4 text-xs text-gray-400" data-id="element-226">
                  <LockIcon className="w-3 h-3" data-id="element-227" />
                  <span data-id="element-228">{t.leadForm.security}</span>
                </div>
              </form>}
          </div>
        </div>
      </div>
    </section>;
};
export default LeadForm;